import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useUpdateMutation } from "../../api/genericHooks";
import { LanguageSelectorModalButton } from "../../components/Buttons/LanguageSelectorModalButton";
import { logo } from "../../images";

type ResetPasswordConfirmData = {
  reset_password_token: string;
  password: string;
  password_confirmation: string;
};

export const ResetPasswordConfirm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();

  // Get the reset_password_token from the query string
  const reset_password_token = new URLSearchParams(search).get(
    "reset_password_token"
  );

  // Query Methods
  const resetPasswordQuery = useUpdateMutation("/password");

  // Form Methods
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ResetPasswordConfirmData>();

  // Set the reset_password_token in the form
  useEffect(() => {
    if (reset_password_token) {
      setValue("reset_password_token", reset_password_token);
    }
  }, [reset_password_token]);

  const onSubmit: SubmitHandler<ResetPasswordConfirmData> = (data) => {
    resetPasswordQuery.mutateAsync({ user: data }).then(() => {
      navigate("/");
      toast.success(t("resetPassword.passwordResetSuccess"));
    });
  };

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-delicate-50">
        <div className="w-full justify-items-end bg-delicate-50 sm:block">
          <LanguageSelectorModalButton />
        </div>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            alt="Memoirs Logo"
            src={logo.logoGrey}
            className="mx-auto h-20 w-auto"
          />
          <h2 className="mt-6 font-poppins text-center text-2xl/9 font-bold tracking-tight text-slate-100">
            {t("resetPassword.resetPassword")}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label
                  htmlFor="password"
                  className="block font-nunito text-sm/6 font-medium text-slate-900"
                >
                  {t("resetPassword.password")}
                </label>
                <div className="mt-2">
                  <input
                    id="password"
                    type="password"
                    className="block w-full font-nunito rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400 sm:text-sm/6"
                    {...register("password", { required: true })}
                  />
                  {errors.password && (
                    <span className="text-rust-500 font-nunito">
                      {t("errors.required")}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label
                  htmlFor="password_confirmation"
                  className="block font-nunito text-sm/6 font-medium text-slate-900"
                >
                  {t("resetPassword.passwordConfirmation")}
                </label>
                <div className="mt-2">
                  <input
                    id="password_confirmation"
                    type="password"
                    className="block w-full font-nunito rounded bg-white px-3 py-1.5 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400 sm:text-sm/6"
                    {...register("password_confirmation", { required: true })}
                  />
                  {errors.password_confirmation && (
                    <span className="text-rust-500">
                      {t("errors.required")}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  className="flex w-full font-nunito justify-center rounded bg-rust-400 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-rust-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rust-400"
                >
                  {t("resetPassword.resetPasswordConfirm")}
                </button>
              </div>
            </form>
          </div>

          <p className="mt-10 text-center font-nunito text-sm/6 ">
            <NavLink
              to="/login"
              className="font-semibold font-nunito text-rust-400 hover:text-rust-50"
            >
              {t("resetPassword.backToLogin")}
            </NavLink>
          </p>
        </div>
      </div>
    </>
  );
};
